import React, {type KeyboardEventHandler, type ReactNode, useState} from 'react';
import {
    Button,
    Form, Input, Space
} from 'antd';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';

interface ViewProps {
    id: string
    label: string,

    onPressEnter?: KeyboardEventHandler<HTMLInputElement>;
    getState?: (isValid:boolean, value:string) => void
    type?: 'text' | 'date' | 'datetime-local' | 'email' | 'month' | 'number' | 'password' | 'search' | 'tel' | 'time' | 'url';
    placeholder?: string
    helperText?: string
    isRequired?: boolean
    initialValue?: string
    isReadOnly?:boolean
    isEqualTo?: string
    min?:number
    max?:number
    prefix?: ReactNode
    postElement?: ReactNode
    errorMessage?: string
}

export const DiazarFormInput: React.FunctionComponent<ViewProps> = (props) => {
    const initialValue = ( props.initialValue ) ? props.initialValue : '';

    const [value, setValue] = React.useState<string>( initialValue );
    const [isValidValue, setIsValidValue] = React.useState<boolean>( true );
    const [hasInitialChange, setHasInitialChange] = React.useState<boolean>( false );
    const [seePassword, setSeePassword] = useState<boolean>(false);

    const type = React.useMemo( ()=>{
        let retType = ( props.type ) ? props.type : 'text';

        if( retType === 'password' && seePassword ){
            retType = 'text';
        }

        return retType;
    }, [props.type, seePassword] );

    const MessageText = React.useMemo( ()=>{
        if (props.errorMessage !== undefined && props.errorMessage !== '') {
            return props.errorMessage;
        }

        let message = ( props.helperText ) ? props.helperText : '';

        if( hasInitialChange && !isValidValue ){
            switch ( type ){
                case 'email':
                    message = 'Introdusca un email válido';
                    break;
                default:
                    message = 'Este campo es requerido';
                    break;
            }

            if( props.isEqualTo && value !== props.isEqualTo){
                message = 'Los campos no coinciden';
            }

            if(props.min && value.length < props.min){
                message = `La longitud mínima es de ${ props.min } caracteres`;
            }

            if(props.max && value.length > props.max){
                message = `La longitud máxima es de ${ props.max } caracteres`;
            }
        }

        return message;
    }, [props.errorMessage, props.helperText, props.isEqualTo, props.min, props.max, hasInitialChange, isValidValue, type, value]);

    const ValidateStatus = React.useMemo( ()=>{
        if (props.errorMessage !== undefined && props.errorMessage !== '') {
            return 'error';
        }

        const variant = ( hasInitialChange && !isValidValue ) ?  'error' : '';
        return hasInitialChange && !isValidValue ? variant : '';
    }, [hasInitialChange, isValidValue, props.errorMessage] );

    React.useEffect( ()=>{
        let validAux = true;

        if( props.isRequired ){
            const type = ( props.type ) ? props.type : 'text';

            switch ( type ){
                case 'email': {
                    const isValid = String(value)
                        .toLowerCase()
                        .match(
                            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                        );

                    validAux = isValid !== null;

                    break;
                }
                default:
                    validAux = value.trim() !== '';
                    break;
            }

        }

        if( validAux && props.isEqualTo ){
            validAux = ( value === props.isEqualTo );
        }

        if( validAux && props.min){
            validAux  = ( value.length >= props.min );
        }

        if(validAux && props.max){
            validAux = ( value.length <= props.max );
        }

        setIsValidValue( validAux );

    }, [value, props.isRequired, props.type, props.isEqualTo, props.min, props.max ] );

    React.useEffect( ()=>{
        if( props.getState ){
            props.getState(isValidValue, value);
        }
    }, [value, isValidValue, props] );

    const inputGropPassword = React.useMemo( ()=>{
        const retType = ( props.type ) ? props.type : 'text';

        if( retType !== 'password' ){
            return  null;
        }

        return (
            <Button
                icon={ ( seePassword ) ? <EyeInvisibleOutlined /> : <EyeOutlined /> }
                onClick={ () => setSeePassword( flag => !flag ) }
                danger={ ValidateStatus === 'error' }
                disabled={ props.isReadOnly }
            />
        );
    }, [ValidateStatus, props.isReadOnly, props.type, seePassword] );

    return (
        <Form.Item label={ props.label }
                   required={ props.isRequired }
                   htmlFor={props.id}
                   help={ MessageText }
                   validateStatus={ ValidateStatus }
        >
            <Space.Compact style={ {width:'100%'} }>
                <Input id={props.id}
                       //name={props.id}
                       type={ type }
                       placeholder={ props.placeholder }
                       value={value}
                       onChange={ (event) => { setValue( event.target.value ); } }
                       aria-invalid={ (hasInitialChange && !isValidValue) }
                       onBlur={ ()=>{ setHasInitialChange(true); } }
                       readOnly={ props.isReadOnly }
                       disabled={ props.isReadOnly }
                       min={ props.min }
                       max={ props.max }
                       prefix={ props.prefix }
                       onPressEnter={ props.onPressEnter }
                />
                { props.postElement && props.postElement }
                { inputGropPassword }
            </Space.Compact>

        </Form.Item>
    );
};
